import { useState, useMemo, useRef, useEffect } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { Spinner } from '../../../../catalyst-components/spinner';
import { Badge } from '../../../../catalyst-components/badge';
import { DailyStatsChart } from '../../../../shared/components/DailyStatsChart';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../catalyst-components/table';

type TabId = 'mindset' | 'sessions' | 'chat' | 'acorns';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'mindset', label: 'Mindset Scores', icon: '🧠' },
  { id: 'sessions', label: 'Sessions', icon: '📋' },
  { id: 'chat', label: 'Chat Activity', icon: '💬' },
  { id: 'acorns', label: 'Acorns', icon: '🌰' },
];

// ─── Searchable User Filter ───
const getUserDisplayName = (u: { firstName?: string | null; lastName?: string | null; email?: string }) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '';

const UserSearchFilter = ({ users, selectedUserId, onSelect }: {
  users: Array<{ _id: string; firstName?: string | null; lastName?: string | null; email?: string }> | undefined;
  selectedUserId: string;
  onSelect: (userId: string) => void;
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        getUserDisplayName(u).toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const selectedUser = useMemo(() => {
    if (selectedUserId === 'all' || !users) return null;
    return users.find((u) => u._id === selectedUserId) || null;
  }, [users, selectedUserId]);

  return (
    <div className="mb-6 flex items-center gap-3 flex-wrap">
      <label className="text-sm font-medium text-muted-foreground">Filter by user:</label>
      <div ref={containerRef} className="relative min-w-[280px]">
        <input
          type="text"
          placeholder={selectedUser ? getUserDisplayName(selectedUser) : 'Search users...'}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
            selectedUser ? 'text-foreground' : 'text-foreground'
          }`}
        />
        {isOpen && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
            <button
              onClick={() => {
                onSelect('all');
                setSearch('');
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                selectedUserId === 'all' ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
              }`}
            >
              All Users
            </button>
            {filteredUsers.map((u) => (
              <button
                key={u._id}
                onClick={() => {
                  onSelect(u._id);
                  setSearch('');
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                  selectedUserId === u._id ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
                }`}
              >
                <span>{getUserDisplayName(u) || 'Unnamed'}</span>
                <span className="text-xs text-muted-foreground ml-2">{u.email}</span>
              </button>
            ))}
            {filteredUsers.length === 0 && (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">No users found</div>
            )}
          </div>
        )}
      </div>
      {selectedUser && (
        <div className="flex items-center gap-2">
          <Badge color="blue">{getUserDisplayName(selectedUser)}</Badge>
          <button
            onClick={() => {
              onSelect('all');
              setSearch('');
            }}
            className="text-muted-foreground hover:text-foreground text-sm"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export const UserProgress = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('mindset');
  const [selectedUserId, setSelectedUserId] = useState<string>('all');

  // Fetch data for all tabs
  const allUsers = useQuery(api.users.getAllUsers);
  const mindsetData = useQuery(api.admin_analytics.getAllMindsetScoreHistory);
  const sessionsData = useQuery(api.admin_analytics.getCompletedSessionStats);
  const chatData = useQuery(api.admin_analytics.getEvergreenMessageStats);
  const acornData = useQuery(api.admin_analytics.getAcornTransactionStats);

  const handleBackClick = () => navigate('/admin');

  // ─── Mindset Tab ───
  const mindsetChartData = useMemo(() => {
    if (!mindsetData) return [];
    const filtered =
      selectedUserId === 'all'
        ? mindsetData
        : mindsetData.filter((r) => r.user_id === selectedUserId);

    const byDate = new Map<string, { total: number; count: number }>();
    for (const r of filtered) {
      const day = r.recorded_at.split('T')[0];
      const existing = byDate.get(day);
      if (existing) {
        existing.total += r.composite_score;
        existing.count++;
      } else {
        byDate.set(day, { total: r.composite_score, count: 1 });
      }
    }
    return Array.from(byDate.entries())
      .map(([date, { total, count }]) => ({
        date,
        count: Math.round((total / count) * 10) / 10,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [mindsetData, selectedUserId]);

  const mindsetTableData = useMemo(() => {
    if (!mindsetData) return [];
    const filtered =
      selectedUserId === 'all'
        ? mindsetData
        : mindsetData.filter((r) => r.user_id === selectedUserId);
    return [...filtered]
      .sort((a, b) => b.recorded_at.localeCompare(a.recorded_at))
      .slice(0, 50);
  }, [mindsetData, selectedUserId]);

  // ─── Sessions Tab ───
  const sessionsChartData = useMemo(() => {
    if (!sessionsData) return [];
    const filtered =
      selectedUserId === 'all'
        ? sessionsData
        : sessionsData.filter((s) => s.user_id === selectedUserId);

    const byDate = new Map<string, number>();
    for (const s of filtered) {
      if (!s.completed_timestamp) continue;
      const day = s.completed_timestamp.split('T')[0];
      byDate.set(day, (byDate.get(day) || 0) + 1);
    }
    return Array.from(byDate.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [sessionsData, selectedUserId]);

  const sessionsTableData = useMemo(() => {
    if (!sessionsData) return [];
    const filtered =
      selectedUserId === 'all'
        ? sessionsData
        : sessionsData.filter((s) => s.user_id === selectedUserId);
    return filtered.slice(0, 50);
  }, [sessionsData, selectedUserId]);

  // ─── Chat Tab ───
  const chatChartData = useMemo(() => {
    if (!chatData) return [];
    if (selectedUserId === 'all') {
      return chatData.dailyData;
    }
    return chatData.perUserDailyData[selectedUserId] || [];
  }, [chatData, selectedUserId]);

  const chatTableData = useMemo(() => {
    if (!chatData) return [];
    if (selectedUserId === 'all') return chatData.userStats;
    return chatData.userStats.filter((s) => s.user_id === selectedUserId);
  }, [chatData, selectedUserId]);

  // ─── Acorns Tab ───
  const acornChartData = useMemo(() => {
    if (!acornData) return { received: [] as { date: string; count: number }[], spent: [] as { date: string; count: number }[] };
    const filtered =
      selectedUserId === 'all'
        ? acornData
        : acornData.filter((t) => t.user_id === selectedUserId);

    const receivedByDate = new Map<string, number>();
    const spentByDate = new Map<string, number>();

    for (const t of filtered) {
      const day = t.timestamp.split('T')[0];
      if (t.amount > 0) {
        receivedByDate.set(day, (receivedByDate.get(day) || 0) + t.amount);
      } else {
        spentByDate.set(day, (spentByDate.get(day) || 0) + Math.abs(t.amount));
      }
    }

    const allDates = new Set([...receivedByDate.keys(), ...spentByDate.keys()]);
    const sortedDates = Array.from(allDates).sort();

    return {
      received: sortedDates.map((date) => ({
        date,
        count: receivedByDate.get(date) || 0,
      })),
      spent: sortedDates.map((date) => ({
        date,
        count: spentByDate.get(date) || 0,
      })),
    };
  }, [acornData, selectedUserId]);

  const acornTableData = useMemo(() => {
    if (!acornData) return [];
    const filtered =
      selectedUserId === 'all'
        ? acornData
        : acornData.filter((t) => t.user_id === selectedUserId);
    return filtered.slice(0, 50);
  }, [acornData, selectedUserId]);

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Per-tab loading checks
  const isTabLoading = (tab: TabId) => {
    switch (tab) {
      case 'mindset': return !mindsetData;
      case 'sessions': return !sessionsData;
      case 'chat': return !chatData;
      case 'acorns': return !acornData;
    }
  };

  return (
    <section className="h-screen bg-background pt-24 flex flex-col">
      {/* Fixed header area */}
      <div className="flex-shrink-0 px-6">
        {/* Header */}
        <div className="flex items-center mb-4">
          <Button outline color="dark/zinc" onClick={handleBackClick} className="mr-4">
            ← Back
          </Button>
          <h1 className="text-3xl font-bold">User Analytics</h1>
        </div>

        {/* User Filter */}
        <UserSearchFilter
          users={allUsers}
          selectedUserId={selectedUserId}
          onSelect={setSelectedUserId}
        />

        {/* Tabs */}
        <div className="flex border-b border-border gap-1 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable tab content */}
      <div className="flex-1 overflow-y-auto px-6 pt-6">
        <div className="space-y-6 pb-12">
        {/* Per-tab spinner */}
        {isTabLoading(activeTab) && (
          <div className="flex justify-center items-center h-64">
            <Spinner />
          </div>
        )}

        {/* ═══════════════ MINDSET TAB ═══════════════ */}
        {activeTab === 'mindset' && mindsetData && (
          <>
            <DailyStatsChart
              data={mindsetChartData}
              title="Composite Mindset Score Over Time"
              lineColor="#8b5cf6"
              yAxisLabel="Score"
              tooltipSuffix="avg score"
            />
            <div className="border border-border rounded-lg overflow-hidden">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Date</TableHeader>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Composite</TableHeader>
                    <TableHeader>N</TableHeader>
                    <TableHeader>WB</TableHeader>
                    <TableHeader>SE</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mindsetTableData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No mindset score data
                      </TableCell>
                    </TableRow>
                  )}
                  {mindsetTableData.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{formatDateTime(r.recorded_at)}</TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{r.user_name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{r.user_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge color="purple">{r.composite_score.toFixed(1)}</Badge>
                      </TableCell>
                      <TableCell>{r.n_score.toFixed(1)}</TableCell>
                      <TableCell>{r.wb_score.toFixed(1)}</TableCell>
                      <TableCell>{r.se_score.toFixed(1)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* ═══════════════ SESSIONS TAB ═══════════════ */}
        {activeTab === 'sessions' && sessionsData && (
          <>
            <DailyStatsChart
              data={sessionsChartData}
              title="Sessions Completed Per Day"
              lineColor="#10b981"
              yAxisLabel="Sessions"
              tooltipSuffix="sessions"
            />
            <div className="border border-border rounded-lg overflow-hidden">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Date</TableHeader>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Skill</TableHeader>
                    <TableHeader>Session</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionsTableData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No completed sessions
                      </TableCell>
                    </TableRow>
                  )}
                  {sessionsTableData.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell>{formatDateTime(s.completed_timestamp)}</TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{s.user_name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{s.user_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {s.skill_name ? (
                          <Badge color="green">{s.skill_name}</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>{s.assistant_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* ═══════════════ CHAT TAB ═══════════════ */}
        {activeTab === 'chat' && chatData && (
          <>
            <DailyStatsChart
              data={chatChartData}
              title="User Messages Per Day (Evergreen Chat)"
              lineColor="#3b82f6"
              yAxisLabel="Messages"
              tooltipSuffix="messages"
            />
            <div className="border border-border rounded-lg overflow-hidden">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Total Messages</TableHeader>
                    <TableHeader>Last Active</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chatTableData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                        No chat activity
                      </TableCell>
                    </TableRow>
                  )}
                  {chatTableData.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{s.user_name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{s.user_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge color="blue">{s.message_count}</Badge>
                      </TableCell>
                      <TableCell>{formatDateTime(s.latest_message_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* ═══════════════ ACORNS TAB ═══════════════ */}
        {activeTab === 'acorns' && acornData && (
          <>
            <div className="space-y-4">
              <DailyStatsChart
                data={acornChartData.received}
                title="Acorns Received Per Day"
                lineColor="#10b981"
                yAxisLabel="Acorns"
                tooltipSuffix="acorns received"
              />
              <DailyStatsChart
                data={acornChartData.spent}
                title="Acorns Spent Per Day"
                lineColor="#ef4444"
                yAxisLabel="Acorns"
                tooltipSuffix="acorns spent"
              />
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Date</TableHeader>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Type</TableHeader>
                    <TableHeader>Amount</TableHeader>
                    <TableHeader>Balance After</TableHeader>
                    <TableHeader>Description</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {acornTableData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No acorn transactions
                      </TableCell>
                    </TableRow>
                  )}
                  {acornTableData.map((t, i) => (
                    <TableRow key={i}>
                      <TableCell>{formatDateTime(t.timestamp)}</TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{t.user_name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{t.user_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge color={t.amount > 0 ? 'green' : 'red'}>{t.type}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className={t.amount > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                          {t.amount > 0 ? '+' : ''}{t.amount}
                        </span>
                      </TableCell>
                      <TableCell>{t.balance_after}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                        {t.description}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        </div>
      </div>
    </section>
  );
};
